import { CurrencyTypeProvider, FormattedCurrency } from "@dgs/core";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { HotelRoomPriceResource, HotelRoomResource } from "../../../shared/types/hotel";
import { ButtonCard, ButtonCardBaseIcon, ButtonCardBody, ButtonCardIcon, ButtonCardIconRow } from "./ButtonCard";

interface Props {
	room: HotelRoomResource;
	isSelected: boolean;
	showRoomCapacityInFrontend: boolean;
	addedAdditionalPersons: boolean;
	onSelect: () => void;
	disabled?: boolean;
}

export const HotelRoomCard: FC<Props> = ({
	room,
	isSelected,
	showRoomCapacityInFrontend,
	addedAdditionalPersons,
	onSelect,
	disabled = false,
}) => {
	const { t } = useTranslation();

	return (
		<ButtonCard
			disabled={disabled}
			role="button"
			aria-label={room.name}
			aria-pressed={isSelected}
			onClick={onSelect}
			tabIndex={0}
		>
			<ButtonCardBody>
				<strong>{room.name}</strong>
				{showRoomCapacityInFrontend && room.capacity > 0 && (
					<ButtonCardIconRow>
						<ButtonCardIcon $icon="person" />
						<div>{t("{{count}} Persons", { count: room.capacity })}</div>
					</ButtonCardIconRow>
				)}
				{room.price && (
					<ButtonCardIconRow>
						<ButtonCardBaseIcon src="/icons/sell.svg" />
						<div>
							<RoomPrice addedAdditionalPersons={addedAdditionalPersons} roomPrice={room.price} />
						</div>
					</ButtonCardIconRow>
				)}
			</ButtonCardBody>
		</ButtonCard>
	);
};

interface RoomPriceProps {
	roomPrice: HotelRoomPriceResource;
	addedAdditionalPersons: boolean;
}

const RoomPrice: FC<RoomPriceProps> = ({ roomPrice, addedAdditionalPersons }) => {
	const { t } = useTranslation();
	let price: ReactNode;

	if (roomPrice.min === roomPrice.max) {
		price = <FormattedCurrency value={roomPrice.min} />;
	} else {
		price = (
			<>
				<FormattedCurrency value={roomPrice.min} /> - <FormattedCurrency value={roomPrice.max} />
			</>
		);
	}

	return (
		<CurrencyTypeProvider currencyType={roomPrice.currency}>
			{addedAdditionalPersons ? t("from") + " " : ""}
			{price} <small>{t("per Night")}</small>
		</CurrencyTypeProvider>
	);
};
